import { Controller, useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Button from 'design-system/src/components/Button/Button';
import { Input } from '../Input.tsx';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { ErrorMessage } from 'design-system/src/components/ErrorMessage/ErrorMessage';
import { SignupErrorMessage } from './SignupErrorMessage';
import { getRequiredErrorMessage } from './utils.tsx';
import { PHONE_VALIDATION_REGEX } from '../../constants';
import { formatPhoneNumberIncrementally, phoneNumberUnformatted, handleServerError } from '../../utils';
import { StyledSignupForm } from './styles.ts';
import { PageContainerWithLogo } from './PageContainerWithLogo.tsx';
import { client } from '../../api.tsx';
import { ProgressCard } from 'design-system/src/components/ProgressCard/ProgressCard';
import { useState } from 'react';
import { createTrackingProps } from 'shared-utils/analytics';

type AddProviderAndPhoneNumberFormData = {
  npi: string;
  officePhoneNumber: string;
  faxNumber: string;
};

export type AddProviderAndPhoneNumberProps = {
  className?: string;
  onNext: () => void;
  defaultValues?: {
    npi?: string;
    office_phone_number?: string;
    fax_number?: string;
  };
  onFormDataUpdate?: (data: { npi?: string; office_phone_number?: string; fax_number?: string }) => void;
};

export const AddProviderAndPhoneNumber = ({
  className,
  onNext,
  defaultValues,
  onFormDataUpdate,
}: AddProviderAndPhoneNumberProps) => {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<AddProviderAndPhoneNumberFormData>({
    defaultValues: {
      npi: defaultValues?.npi || '',
      officePhoneNumber: defaultValues?.office_phone_number || '',
      faxNumber: defaultValues?.fax_number || '',
    },
  });

  const { mutate: addProvider, isPending: isAddingProvider } = useMutation({
    mutationFn: async (data: AddProviderAndPhoneNumberFormData) => {
      const response = await client.post('/signup/add-provider-and-phone-number', {
        npi: data.npi,
        office_phone_number: phoneNumberUnformatted(data.officePhoneNumber),
        fax_number: phoneNumberUnformatted(data.faxNumber),
      });
      return response.data;
    },
    onSuccess: async (_, variables) => {
      setError(null);

      const providerData = {
        npi: variables.npi,
        office_phone_number: phoneNumberUnformatted(variables.officePhoneNumber),
        fax_number: phoneNumberUnformatted(variables.faxNumber),
      };
      onFormDataUpdate?.(providerData);

      // Invalidate and refetch user data to get updated account_setup_complete status
      await queryClient.invalidateQueries({ queryKey: ['checkLogin'] });

      onNext();
    },
    onError: (error: any) => {
      console.error('Error adding provider and phone number:', error);
      const serverErrorMessage = handleServerError(error);
      setError(serverErrorMessage);
    },
  });

  // We need this wrapper function to satisfy the expected input type of handleSubmit
  const handleFormSubmit = (data: AddProviderAndPhoneNumberFormData) => {
    addProvider(data);
  };

  return (
    <PageContainerWithLogo className={className}>
      <ProgressCard progressBar={{ current: 3, total: 3 }}>
        <StyledSignupForm onSubmit={handleSubmit(handleFormSubmit)}>
          <div>
            <Typography styledAs="h5" weight={500} marginBottom="8px">
              Set up your provider to start prescribing
            </Typography>
            <Typography styledAs="bodySmallDMSans" color="var(--grayscale-600)">
              We'll verify this provider so you can start sending prescriptions. You can always add more providers and
              offices later.
            </Typography>
          </div>

          <Controller
            control={control}
            name="npi"
            rules={{
              required: { value: true, message: getRequiredErrorMessage('NPI') },
              pattern: {
                value: /^\d{10}$/,
                message: 'NPI must be exactly 10 digits',
              },
            }}
            render={({ field }) => (
              <Input
                label="Provider NPI Number"
                placeholder="1234567890"
                type="text"
                inputMode="numeric"
                maxLength={10}
                hasError={!!errors.npi}
                {...field}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const onlyDigits = e.target.value.replace(/\D/g, '');
                  field.onChange(onlyDigits);
                  setError(null);
                }}
                {...createTrackingProps({ unmask: true })}
              />
            )}
          />
          {errors.npi && <ErrorMessage>{errors.npi.message}</ErrorMessage>}

          <Controller
            control={control}
            name="officePhoneNumber"
            rules={{
              required: { value: true, message: getRequiredErrorMessage('Office phone number') },
              pattern: {
                value: PHONE_VALIDATION_REGEX,
                message: 'Invalid phone number format',
              },
            }}
            render={({ field }) => (
              <Input
                label="Office Phone Number"
                placeholder="(555) 123-4567"
                type="tel"
                hasError={!!errors.officePhoneNumber}
                {...field}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const formatted = formatPhoneNumberIncrementally(e.target.value);
                  field.onChange(formatted);
                  setError(null);
                }}
                {...createTrackingProps({ unmask: true })}
              />
            )}
          />
          {errors.officePhoneNumber && <ErrorMessage>{errors.officePhoneNumber.message}</ErrorMessage>}

          <Controller
            control={control}
            name="faxNumber"
            rules={{
              required: { value: true, message: getRequiredErrorMessage('Fax number') },
              pattern: {
                value: PHONE_VALIDATION_REGEX,
                message: 'Invalid fax number format',
              },
            }}
            render={({ field }) => (
              <Input
                label="Fax Number"
                placeholder="(555) 987-6543"
                type="tel"
                hasError={!!errors.faxNumber}
                {...field}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const formatted = formatPhoneNumberIncrementally(e.target.value);
                  field.onChange(formatted);
                  setError(null);
                }}
                {...createTrackingProps({ unmask: true })}
              />
            )}
          />
          {errors.faxNumber && <ErrorMessage>{errors.faxNumber.message}</ErrorMessage>}

          {error && <SignupErrorMessage error={error} marginBottom="16px" />}

          <Button size="lg" width="full" type="submit" isLoading={isAddingProvider}>
            Get started
          </Button>
        </StyledSignupForm>
      </ProgressCard>
    </PageContainerWithLogo>
  );
};

export default AddProviderAndPhoneNumber;
